import { createRoom, listOrganizations, listRooms } from '#~/api/agent/index.js';
import { deleteRoom } from '#~/api/agent/registry.js';
import { toHexFromId } from '#~/api/index.js';
import * as persistenceLayer from '#~/persistence/index.js';
import { initAccount } from '#~/room/index.js';
import type { Room } from '@studio/models/room';
import type { AgentRegistryGetRoomResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';

export type EnsureDefaultOrgAndRoomResult = {
  createdOrganization: boolean;
  createdRoom: boolean;
  /** The ID of the newly created room, if one was created. Undefined otherwise. */
  newRoomId?: string;
};

export type EnsureDefaultOrgAndRoomOptions = {
  /**
   * If true, always creates a new room even if rooms already exist.
   * Useful for auto-linking scenarios where each computer should get its own studio.
   * Default: false
   */
  alwaysCreateRoom?: boolean;
};

// In-memory cache to prevent concurrent organization creation attempts
const orgCreationCache = new Map<string, Promise<EnsureDefaultOrgAndRoomResult>>();
const ORG_CREATION_TIMEOUT = 30_000; // 30 seconds

/**
 * ENG-4154 cross-boot self-heal. The default-room bootstrap is a fallback
 * that can race link flows ACROSS boots: a fresh login can terminally fail
 * auto-link (sidecar has no token yet), the bootstrap creates a user-named
 * fallback room, and the sidecar then links on the NEXT boot — creating the
 * real device room and leaving the fallback as a duplicate studio. We record
 * every fallback room we create; once a linked room exists, the sweep
 * deletes the recorded room iff it is still agentless.
 */
export const PERSISTENCE_KEY_BOOTSTRAP_ROOM = 'bootstrap-default-room';

export interface BootstrapRoomRecord {
  /**
   * Hex room id of the fallback room this client created. Absent on the
   * write-ahead intent record persisted BEFORE creation — a reboot/crash in
   * the create window (observed: warm account-switch reboot) must not leave
   * an unrecorded, unsweepable room behind.
   */
  readonly roomId?: string;
  /** Exact studio name the bootstrap is about to create — intent-record match key. */
  readonly studioName?: string;
  /** auth0 sub of the user it was created for (same keying as the auto-link completion record). */
  readonly userId: string;
  readonly createdAt: number;
}

// Intent records (no roomId) match by exact name only within this window —
// outside it, a same-named user-created room must never be swept.
const INTENT_RECORD_MATCH_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Best-effort: a lost record only means the sweep won't run — never fatal. */
export const recordBootstrapRoom = async (record: BootstrapRoomRecord): Promise<void> => {
  try {
    await persistenceLayer.set(PERSISTENCE_KEY_BOOTSTRAP_ROOM, JSON.stringify(record));
  } catch (error) {
    console.warn('recordBootstrapRoom: unable to persist record', error);
  }
};

/**
 * Delete the recorded fallback room now that a linked room exists.
 * Returns the swept hex room id so the caller can drop it from its local
 * rooms list, or undefined when nothing was deleted. Deletion is strictly
 * scoped: only the room WE recorded, only for the same user, only while it
 * has no agents (an adopted/used room clears the record instead).
 */
export const sweepStaleBootstrapRoom = async ({
  rooms,
  linkedRoomId,
  userId,
  token
}: {
  rooms: AgentRegistryGetRoomResponse[];
  linkedRoomId: string;
  userId: string;
  token: string;
}): Promise<string | undefined> => {
  let record: BootstrapRoomRecord | undefined;

  try {
    const raw = await persistenceLayer.get(PERSISTENCE_KEY_BOOTSTRAP_ROOM);
    if (!raw) {
      return undefined;
    }
    record = JSON.parse(raw) as BootstrapRoomRecord;
  } catch (error) {
    console.warn('sweepStaleBootstrapRoom: unable to read record', error);
    return undefined;
  }

  // Records from another account stay untouched — that user's boot owns them.
  if (!record || record.userId !== userId) {
    return undefined;
  }

  const clearRecord = async (): Promise<void> => {
    try {
      await persistenceLayer.remove(PERSISTENCE_KEY_BOOTSTRAP_ROOM);
    } catch (error) {
      console.warn('sweepStaleBootstrapRoom: unable to clear record', error);
    }
  };

  // The link flow adopted the room we created — it IS the real studio now.
  if (record.roomId !== undefined && record.roomId === linkedRoomId) {
    await clearRecord();
    return undefined;
  }

  let room: AgentRegistryGetRoomResponse | undefined;

  if (record.roomId !== undefined) {
    room = rooms.find((r) => toHexFromId(r.id) === record.roomId);
  } else if (record.studioName) {
    // Intent record: creation (or the post-create record update) was cut
    // short — match by the exact name we were about to create, never the
    // linked room, and only within the freshness window so a same-named
    // room the user creates later is never touched.
    if (Date.now() - record.createdAt > INTENT_RECORD_MATCH_WINDOW_MS) {
      await clearRecord();
      return undefined;
    }

    room = rooms.find((r) => r.name === record.studioName && toHexFromId(r.id) !== linkedRoomId);
  } else {
    // Unidentifiable record — nothing safe to do with it.
    await clearRecord();
    return undefined;
  }

  // Not visible in this list: either already deleted or the list is stale.
  // Keep the record — a later boot with a fresher list retries; a deleted
  // room simply never matches again.
  if (!room) {
    return undefined;
  }

  // Someone put agents in it — it's a real studio now, never delete.
  if ((room.agents ?? []).length > 0) {
    await clearRecord();
    return undefined;
  }

  const roomHex = toHexFromId(room.id);

  if (!roomHex) {
    return undefined;
  }

  try {
    await deleteRoom(roomHex as Room['id'], token);
  } catch (error) {
    // Registry weather: keep the record so the next boot retries the sweep.
    console.warn('sweepStaleBootstrapRoom: delete failed, will retry next boot', error);
    return undefined;
  }

  await clearRecord();
  return roomHex;
};

/**
 * Derives the final organization name from provided parameters
 */
const deriveOrgName = (studioName?: string, organizationName?: string): string => {
  return (
    organizationName ||
    (studioName
      ? `${studioName.endsWith('s') ? studioName : studioName + 's'} Organization`
      : 'My Organization')
  );
};

/**
 * Helper function to create a cache key for deduplication
 */
const createCacheKey = (token: string, studioName?: string, organizationName?: string): string => {
  const tokenParts = token.split('.');
  let userHash = 'unknown';
  if (tokenParts.length > 1 && tokenParts[1]) {
    try {
      const normalized = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const payload = JSON.parse(
        typeof globalThis.atob === 'function'
          ? globalThis.atob(padded)
          : Buffer.from(padded, 'base64').toString('utf-8')
      );
      userHash = payload.sub ?? userHash;
    } catch (error) {
      console.warn('ensureDefaultOrgAndRoom: unable to extract user id from token payload', error);
    }
  }

  const finalOrgName = deriveOrgName(studioName, organizationName);

  return `${userHash}-${finalOrgName}`;
};

/**
 * Ensure the current user has a default Organization and Room they can own/control.
 * If the user has no organization with canCreateRoom permission, create one along with a default room.
 * Enhanced with deduplication to prevent duplicate organization creation.
 *
 * Prefer calling this after new-account signup and after invite redemption for new users.
 *
 * @param token - Auth token
 * @param studioName - Optional studio/room name
 * @param organizationName - Optional organization name
 * @param options - Configuration options
 */
export const ensureDefaultOrgAndRoom = async (
  token: string,
  studioName: string,
  organizationName?: string,
  options?: EnsureDefaultOrgAndRoomOptions
): Promise<EnsureDefaultOrgAndRoomResult> => {
  const { alwaysCreateRoom = false } = options ?? {};
  const cacheKey = createCacheKey(token, studioName, organizationName);

  // Skip cache if alwaysCreateRoom is true, since we want a fresh room each time
  const existingPromise = !alwaysCreateRoom ? orgCreationCache.get(cacheKey) : undefined;
  if (existingPromise) {
    try {
      return await existingPromise;
    } catch (error) {
      // If the cached promise failed, we'll retry below
      orgCreationCache.delete(cacheKey);
    }
  }

  const creationPromise = (async (): Promise<EnsureDefaultOrgAndRoomResult> => {
    const result: EnsureDefaultOrgAndRoomResult = {
      createdOrganization: false,
      createdRoom: false
    };

    try {
      const orgs = await listOrganizations(token);
      const hasOwnedOrg = orgs.some((o) => o.permissions?.canCreateRoom);

      if (!hasOwnedOrg) {
        const finalOrgName = deriveOrgName(studioName, organizationName);
        // ENG-4154: initAccount returns the created room id — surface it so
        // the bootstrap-room record can identify the room deterministically.
        result.newRoomId = await initAccount(token, studioName, finalOrgName);
        result.createdOrganization = true;
        result.createdRoom = true;
        return result;
      }

      const rooms = await listRooms(token);

      // Create a new room if:
      // 1. No rooms exist, OR
      // 2. alwaysCreateRoom option is true (for auto-linking scenarios)
      if (rooms.length === 0 || alwaysCreateRoom) {
        const targetOrg = orgs.find((org) => org.permissions?.canCreateRoom);

        if (!targetOrg) {
          return result;
        }

        // createRoom now returns { id, alreadyExisted } (ENG-4209); this caller
        // only needs the id, so destructure it — behavior unchanged.
        const { id: newRoomId } = await createRoom(studioName, token, targetOrg.id);
        result.createdRoom = true;
        result.newRoomId = newRoomId;
      }
    } catch (error) {
      throw error;
    }
    return result;
  })();

  orgCreationCache.set(cacheKey, creationPromise);

  setTimeout(() => {
    orgCreationCache.delete(cacheKey);
  }, ORG_CREATION_TIMEOUT);

  return await creationPromise;
};
